import { Type } from '@angular/core';
import { AllowSort } from '../service/factory/allow-sort';
import { ColumnDataType, ColumnVisibility } from './column-definition';
import { ColumnItem } from './selectable-items';

export declare interface ListColumn<TSource = any, TValue = any, TValueOriginal = TValue> extends ColumnItem {
  caption: string;
  comparer?: (value1: TValue | TValueOriginal, value2: TValue | TValueOriginal) => number;
  comparisonTarget?: 'Converted' | 'Original';
  converter?: (value: TValueOriginal) => TValue;
  sum: boolean;
  viewType: Type<any>;
  helpBuilder?: (source: TSource) => string;
  canSort?: boolean;
  isPhoneNumber?: boolean;
  dataType?: ColumnDataType;
}

export default class ListViewColumn<TSource = any, TValue = any, TValueOriginal = TValue>
  implements ListColumn<TSource, TValue, TValueOriginal>
{
  constructor(
    binding: string,
    caption: string,
    visibility: ColumnVisibility,
    comparer: (value1: TValue | TValueOriginal, value2: TValue | TValueOriginal) => number,
    readonly helpBuilder: (source: TSource) => string,
    helpText: string,
    private allowSort: AllowSort,
    readonly serverSideSortBinding: string,
    readonly isPhoneNumber?: boolean
  ) {
    this.binding = binding;
    this.caption = caption;
    this.selected = visibility !== ColumnVisibility.optional;
    this.helpText = helpText;
    this.isPhoneNumber = isPhoneNumber;
    if (comparer) {
      this.comparer = comparer;
      this.compareDefined = true;
    } else {
      this.comparer = (s1: TValue | TValueOriginal, s2: TValue | TValueOriginal) => {
        if (s1 && !s2) {
          return 1;
        }
        if (!s1 && s2) {
          return -1;
        }
        if (!s1 && !s2) {
          return 0;
        }
        if (typeof s1 == 'number' && typeof s2 == 'number') {
          if (s1 > s2) {
            return 1;
          } else if (s1 < s2) {
            return -1;
          } else {
            return 0;
          }
        }
        if (typeof s1 == 'string' && typeof s2 == 'string') {
          return s1.localeCompare(s2);
        }
        if (typeof s1 === 'object' && 'localeCompare' in s1 && typeof s1.localeCompare === 'function') {
          return s1.localeCompare(s2);
        }
      };
    }
    this.id = this._getId(caption);
  }
  readonly id: number;
  readonly binding: string;
  readonly caption: string;
  readonly helpText: string;
  private compareDefined = false;
  get canSort(): boolean {
    if (this.allowSort && !this.allowSort.allowSort(this)) {
      // TODO: depricate AllowSort and introduce isComputed:boolean instead
      return false;
    }
    return this.compareDefined || !this.viewType;
  }
  comparer: (value1: TValue | TValueOriginal, value2: TValue | TValueOriginal) => number;
  comparisonTarget: 'Converted' | 'Original' = 'Converted';
  converter: (value: TValueOriginal) => TValue;
  locale: string;
  selected: boolean;
  sum: boolean;
  viewType: Type<any>;
  dataType?: ColumnDataType;

  private _getId(caption: string) {
    const items = (caption || '').split('.');
    let i = '';
    for (let item of items) {
      const codes = item.split('').map(ch => ch.charCodeAt(0));
      const sum = codes.length ? codes.reduce((a, b) => a + b, 0) : 0;
      i += String(sum);
    }

    let result = Number.parseInt(i);
    const MAX_INT = 2147483647;

    if (result > MAX_INT) {
      const truncatedId = i.slice(-9);
      result = Number.parseInt(truncatedId);
    }

    return result;
  }
}
